import { type Answers } from "@/schema/quizSchema";
import { defineLinearQuiz } from "@/schema/defineLinearQuiz";
import { ScoreGauge } from "@/components/ScoreGauge";
import { ResultScreen } from "@/components/ResultScreen";

const PDP = "https://baiafood.com/products/microbiotic-creamer";

// Health points per single-choice option (higher = healthier). 8 scored questions x 3 = 24 max.
// q_sexo and q_edad are demographic (absent here ⇒ contribute 0). This single map is the source
// of truth for BOTH the gauge (computeScore) and the classifier (defineLinearQuiz scoring.points).
const HEALTH_POINTS: Record<string, Record<string, number>> = {
  q_molestias: { a_diario: 0, varias_semana: 1, ocasional: 2, nunca: 3 },
  q_bano: { diario: 3, cada_dos_dias: 2, menos_3_semana: 1, semana_sin: 0 },
  q_deposiciones: { tipo_1: 0, tipo_2: 1, tipo_3: 3, tipo_4: 3, tipo_5: 2, tipo_6: 1, tipo_7: 0 },
  q_gases: { nunca: 3, rara_vez: 2, frecuente: 1, a_diario: 0 },
  q_fibra: { diario: 3, frecuente: 2, casi_nunca: 1, nunca: 0 },
  q_ultraprocesados: { diario: 0, frecuente: 1, casi_nunca: 2, nunca: 3 },
  q_estres: { inexistente: 3, bajo: 3, normal: 2, alto: 1, muy_alto: 0 },
  q_sueno: { muy_bien: 3, bien: 3, regular: 2, mal: 1, muy_mal: 0 },
};

const MAX_POINTS = 24; // 8 scored questions x 3

/**
 * 0..100 gut-health score (higher = healthier), shown on the result gauge. Sums the same
 * HEALTH_POINTS the declarative classifier uses, then normalizes — so the displayed number and
 * the chosen tier are driven by one point map. The classifier's raw-sum thresholds (10, 18) are
 * the integer pre-images of the 40/75 normalized band edges, so number and tier never disagree.
 */
export function computeScore(answers: Answers): number {
  let sum = 0;
  for (const [qid, map] of Object.entries(HEALTH_POINTS)) {
    const v = answers[qid];
    if (typeof v === "string") sum += map[v] ?? 0;
  }
  return Math.round((sum / MAX_POINTS) * 100);
}

export const baiaMicrobiotaQuiz = defineLinearQuiz({
  id: "baia-microbiota",
  slug: "microbiota",
  tenant: "baia",
  meta: {
    locale: "es",
    title: "Test de salud intestinal — BAÏA",
    introHeadline: "¡Descubre el estado de tu microbiota en 2 minutos!",
    introBody: "Responde unas preguntas y descubre en qué estado está tu microbiota y cómo mejorarla.",
  },
  questions: [
    {
      id: "q_sexo",
      type: "single",
      prompt: "¿Cuál es tu sexo?",
      helper: "Las mujeres son más propensas a la hinchazón y el estreñimiento: sus cambios hormonales ralentizan la digestión.",
      options: [
        { value: "mujer", label: "Mujer" },
        { value: "hombre", label: "Hombre" },
      ],
    },
    {
      id: "q_molestias",
      type: "single",
      prompt: "¿Con qué frecuencia experimentas molestias digestivas?",
      helper: "Las molestias digestivas frecuentes suelen ser la primera señal de una microbiota desequilibrada.",
      options: [
        { value: "a_diario", label: "Todos los días" },
        { value: "varias_semana", label: "Varias veces por semana" },
        { value: "ocasional", label: "Ocasionalmente" },
        { value: "nunca", label: "Nunca" },
      ],
    },
    {
      id: "q_bano",
      type: "single",
      prompt: "¿Con qué frecuencia vas al baño?",
      helper: "Lo saludable es ir al baño entre 3 veces al día y 3 veces por semana, sin esfuerzo.",
      options: [
        { value: "diario", label: "Todos los días" },
        { value: "cada_dos_dias", label: "Cada dos días" },
        { value: "menos_3_semana", label: "Menos de 3 veces por semana" },
        { value: "semana_sin", label: "A veces paso una semana sin ir" },
      ],
    },
    {
      id: "q_deposiciones",
      type: "single",
      prompt: "¿Cómo son tus deposiciones (normalmente)?",
      helper: "La escala de Bristol clasifica las heces del tipo 1 al 7; los tipos 3 y 4 son los ideales.",
      options: [
        { value: "tipo_1", label: "Bolas separadas y duras (Tipo 1)" },
        { value: "tipo_2", label: "Como una salchicha con grumos (Tipo 2)" },
        { value: "tipo_3", label: "Como una salchicha con grietas (Tipo 3)" },
        { value: "tipo_4", label: "Como una salchicha lisa y blanda (Tipo 4)" },
        { value: "tipo_5", label: "Piezas blandas con bordes definidos (Tipo 5)" },
        { value: "tipo_6", label: "Piezas blandas y líquidas (Tipo 6)" },
        { value: "tipo_7", label: "Completamente líquidas (Tipo 7)" },
      ],
    },
    {
      id: "q_gases",
      type: "single",
      prompt: "¿Sueles tener gases?",
      helper: "Un exceso de gases puede indicar fermentación por bacterias intestinales desequilibradas.",
      options: [
        { value: "nunca", label: "Nunca" },
        { value: "rara_vez", label: "Rara vez (menos de 1 vez por semana)" },
        { value: "frecuente", label: "Frecuentemente (varias veces por semana)" },
        { value: "a_diario", label: "A diario" },
      ],
    },
    {
      id: "q_fibra",
      type: "single",
      prompt: "¿Con qué frecuencia comes alimentos ricos en fibra?",
      helper: "La fibra es el alimento principal de tus bacterias buenas: sin ella, la microbiota se empobrece.",
      options: [
        { value: "diario", label: "Todos los días" },
        { value: "frecuente", label: "Frecuentemente" },
        { value: "casi_nunca", label: "Casi nunca" },
        { value: "nunca", label: "Nunca" },
      ],
    },
    {
      id: "q_ultraprocesados",
      type: "single",
      prompt: "¿Con qué frecuencia consumes alimentos ultraprocesados?",
      helper: "Los ultraprocesados alimentan a las bacterias menos beneficiosas y reducen la diversidad microbiana.",
      options: [
        { value: "diario", label: "Todos los días" },
        { value: "frecuente", label: "Frecuentemente" },
        { value: "casi_nunca", label: "Casi nunca" },
        { value: "nunca", label: "Nunca" },
      ],
    },
    {
      id: "q_estres",
      type: "single",
      prompt: "¿Cómo describirías tu nivel de estrés actualmente?",
      helper: "El estrés altera el eje intestino-cerebro y puede inflamar tu sistema digestivo.",
      options: [
        { value: "inexistente", label: "Inexistente" },
        { value: "bajo", label: "Bajo" },
        { value: "normal", label: "Normal" },
        { value: "alto", label: "Alto" },
        { value: "muy_alto", label: "Muy alto" },
      ],
    },
    {
      id: "q_sueno",
      type: "single",
      prompt: "¿Cómo duermes normalmente?",
      helper: "Dormir mal desincroniza el reloj de tu microbiota y empeora la digestión.",
      options: [
        { value: "muy_bien", label: "Muy bien (sin interrupciones)" },
        { value: "bien", label: "Bien (despierto 1-2 veces)" },
        { value: "regular", label: "Regular (despierto varias veces)" },
        { value: "mal", label: "Mal (insomnio frecuente)" },
        { value: "muy_mal", label: "Muy mal (casi nunca duermo bien)" },
      ],
    },
    {
      id: "q_edad",
      type: "single",
      prompt: "¿Qué edad tienes?",
      options: [
        { value: "18_24", label: "18-24 años" },
        { value: "25_34", label: "25-34 años" },
        { value: "35_44", label: "35-44 años" },
        { value: "45_54", label: "45-54 años" },
        { value: "55_64", label: "55-64 años" },
        { value: "65_mas", label: "65 años o más" },
        { value: "sin_responder", label: "Prefiero no responder" },
      ],
    },
  ],

  personas: [
    {
      key: "en_alerta",
      label: "🔴 Microbiota en alerta",
      result: {
        tagline: "Tu resultado es bajo: tu microbiota está desequilibrada (disbiosis).",
        body: [
          "Varias de tus respuestas —molestias, tránsito irregular, gases o falta de fibra— apuntan a un desequilibrio claro de tu microbiota intestinal.",
          "La buena noticia: la microbiota se reequilibra cuidando lo que tomas cada día.",
          "Microbiotic Creamer combina fibra prebiótica, probiótico patentado y melena de león para ayudarte a recuperar el control de tu digestión.",
        ],
        cta: { label: "Descubrir Microbiotic Creamer", href: PDP },
      },
    },
    {
      key: "mejorable",
      label: "🟡 Microbiota mejorable",
      result: {
        tagline: "Tienes una base, pero la salud de tu microbiota no es óptima.",
        body: [
          "Pequeños desajustes en tu dieta, tu descanso o tu nivel de estrés están afectando tu bienestar digestivo.",
          "Con unos ajustes estratégicos —más fibra prebiótica y probióticos— verás grandes cambios.",
          "Microbiotic Creamer integra ese cuidado en tu café de cada mañana, sin esfuerzo.",
        ],
        cta: { label: "Descubrir Microbiotic Creamer", href: PDP },
      },
    },
    {
      key: "saludable",
      label: "🟢 Microbiota saludable",
      result: {
        tagline: "Buena base. Ahora toca mantenerla.",
        body: [
          "Tus respuestas reflejan una digestión bastante estable. ¡Enhorabuena!",
          "El equilibrio de la microbiota se mantiene con constancia: fibra prebiótica y probióticos a diario.",
          "Microbiotic Creamer te ayuda a proteger ese equilibrio con un solo hábito cada mañana.",
        ],
        cta: { label: "Conocer Microbiotic Creamer", href: PDP },
      },
    },
  ],

  // Linear branching is derived from question order; threshold scoring is declarative.
  // Raw point sum (max 24) → tier. Thresholds are the integer pre-images of the 0–100 bands:
  // ≥18 (≥75%) saludable · ≥10 (≥~42%) mejorable · else en_alerta (<40%).
  scoring: {
    points: HEALTH_POINTS,
    thresholds: [
      [18, "saludable"],
      [10, "mejorable"],
    ],
    fallback: "en_alerta",
  },

  renderResult: ({ answers, persona, trackCta }) => (
    <>
      <ScoreGauge value={computeScore(answers)} />
      <ResultScreen persona={persona} onCtaClick={trackCta} />
    </>
  ),
});
